// "내 연결 (이 기기)" 의 **상태·명령** — 화면(`MyDeviceInfo`·`MyConnectionSettings`)과 분리한 훅(2026-09-27 첫 화면 개편).
// 첫 화면은 기기 정보(가운데 카드)와 설정(오른쪽 서랍)을 **따로 그리지만 같은 상태**를 써야 한다 → 한 곳(이 훅)에서 한 번만 구독한다.
// 로직은 종전 `MyConnectionPanel` 그대로 옮겼다(주석의 결함 이력 포함).
import { useEffect, useRef, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { SHELL, IS_SHELL } from '../../shell';
import { confirmAction } from '../../ui/confirm';
import { IDENTITY_EVENT, IDENTITY_QUERY, useMyDeviceCode } from './useMyDeviceCode';
import { useMacPerms } from './useMacPerms';

/**
 * 조직 등록 요청의 응답 대기 상한.
 *
 * 🔴 이 값이 없으면 **응답이 영영 오지 않는 구성에서 버튼이 "요청 중…" 에 멈춘 채 아무 말도 하지 않는다**
 * (2026-09-14 무인 서비스 PC 실기 — 명령이 중간에서 버려지고 있었다). 요청은 서버 왕복 한 번이라
 * 정상이면 1초 안에 답이 온다. 넉넉히 기다리되, 기다림이 끝나면 **사실대로** 말한다.
 */
const CLAIM_TIMEOUT_MS = 15000;

/**
 * [보기] 로 드러낸 무인 비밀번호를 다시 가리기까지의 시간.
 *
 * 무인 비밀번호는 **영속 값**이다(세션 비밀번호처럼 매번 바뀌지 않는다) — 확인하려고 띄운 화면에
 * 계속 떠 있으면 그 자리를 지나는 사람이 그대로 읽는다. 확인에 충분하되 잊고 자리를 떠도 곧 닫히는 길이.
 */
const UNATT_REVEAL_MS = 20000;

/**
 * 무인 비밀번호 명령(설정·변경·확인·보기·해제)의 응답 대기 상한.
 *
 * 🔴 `CLAIM_TIMEOUT_MS` 와 같은 이유다. 이 버튼들의 답은 에이전트(무인 PC 라면 **서비스**)가 주는데,
 * 중간 층이 조용히 삼키면 버튼이 잠긴 채 굳어 사용자가 할 수 있는 일이 없어진다
 * (2026-09-14 실기: 이미 설정된 비밀번호를 바꾸자 어댑터가 "바뀐 게 없다" 고 판단해 응답을 삼켰다).
 * 근본 원인은 에이전트 쪽에서 고쳤지만, 화면은 **어떤 이유로 답이 없든** 스스로 풀려야 한다.
 */
const UNATT_TIMEOUT_MS = 15000;

/** 껍데기가 주는 피제어 정체성(이벤트 페이로드 = `current_identity` 반환값). */
export type Identity = {
  deviceCode?: string;
  sessionPassword?: string | null;
  unattendedSet?: boolean;
  /** "다음부터 묻지 않고 접속" 으로 비밀번호 없이 허용한 계정 수. */
  trustedCount?: number;
  /** 원격 입력 제어를 지금 허용하고 있는지(A4 실시간 스위치). */
  controlAllowed?: boolean;
  /** 이 기기가 `--view-only` 로 고정돼 사용자가 스위치를 켤 수 없는지. */
  controlLocked?: boolean;
  /** 이 기기가 등록된 조직들(§14.18 — 여러 조직 소속). 서버가 접속·변경 때마다 전체 목록을 민다. */
  orgs?: OrgEntry[];
};

/** 등록된 조직 한 곳(에이전트 `RS_IDENTITY.orgs` 원소). */
export type OrgEntry = { orgId: string; orgName: string };

// macOS 권한(화면 기록·손쉬운 사용) 상태·판정은 `useMacPerms` 한 곳(2026-10-12 — 로그인 후 경고 띠와 공유).

/**
 * "내 연결 (이 기기)" 상태 훅. 껍데기(Tauri)가 없으면(브라우저) 값이 비어 있고 화면이 스스로 아무것도 그리지 않는다.
 * ⚠️ **로그인 전에도 쓰인다** — 피제어 전용 기기는 계정이 없어 로그인하지 않는다(2026-09-08 실사용).
 */
export function useMyConnection() {
  // ⚠️ **기기 코드도 반응형이어야 한다.** 무인 서비스에 붙은 모드(`unattendedHost`)에서는 앱이 켜질 때
  // 서비스가 아직 안 떠 있을 수 있고, 뜨는 즉시 코드가 도착한다. 주입값만 쓰면 그 창은 영영 빈 채로 남는다.
  // 구독은 공용 훅 한 곳(기기 목록의 "이 PC" 판정과 같은 값 — 2026-09-29).
  const deviceCode = useMyDeviceCode();
  // 세션 비밀번호·무인 설정 상태는 껍데기가 런타임 변경(무재접속)할 수 있어 반응형 상태로 관리한다.
  const [sessionPw, setSessionPw] = useState<string | undefined>(SHELL.sessionPassword);
  const [regenerating, setRegenerating] = useState(false);
  const [unattendedSet, setUnattendedSet] = useState<boolean>(!!SHELL.unattendedSet);
  const [trustedCount, setTrustedCount] = useState<number>(SHELL.trustedCount ?? 0);
  const [unattendedPw, setUnattendedPw] = useState('');
  const [unattendedBusy, setUnattendedBusy] = useState(false);
  // 무인 비밀번호 [확인]/[보기] 결과. 확인은 문구로, 보기는 값 자체로 답한다.
  const [unattNote, setUnattNote] = useState<{ tone: 'ok' | 'warn'; text: string } | undefined>();
  /** [보기] 로 드러난 평문. 화면에만 잠시 둔다(자동으로 다시 가려진다 — 어깨너머 노출 최소화). */
  const [unattShown, setUnattShown] = useState<string | undefined>(undefined);
  /**
   * 무인 비밀번호 화면의 모드. **한 입력칸이 여러 역할을 겸하지 않게** 하는 장치다.
   *   idle   — 설정돼 있을 때의 기본 화면(보기·변경·해제 버튼만)
   *   change — 새 비밀번호를 받는 중(설정되지 않은 기기의 첫 설정도 이 모드다)
   *   verify — 내가 아는 값이 맞는지 확인하는 중(구버전 기기라 [보기]가 안 되는 경우의 대안)
   * 종전에는 칸 하나에 [설정]·[확인]이 함께 붙어 있어 "무엇을 눌러야 바뀌는지" 를 화면이 말하지 않았다.
   */
  const [unattMode, setUnattMode] = useState<'idle' | 'change' | 'verify'>('idle');
  /**
   * 무인 명령의 응답 대기 타이머. 답이 오면 지운다.
   *
   * 🔴 **안전망이다.** 이 버튼들의 결과는 에이전트(무인 PC 라면 서비스)가 돌려주는데, 중간 어느 층이
   * 조용히 삼키면 busy 가 영영 풀리지 않아 **버튼이 잠긴 채 굳는다**(2026-09-14 실기에서 실제로 겪었다).
   * 원인은 따로 고쳤지만, 다른 이유로 답이 없을 때도 화면이 잠기지 않게 시간을 재 둔다(claim 과 같은 방식).
   */
  const unattTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // A4 실시간 입력 제어 스위치. 기본은 허용 — 지원의 대부분이 제어를 필요로 하므로 주 경로에 마찰을 두지 않는다.
  const [controlAllowed, setControlAllowed] = useState<boolean>(SHELL.controlAllowed !== false);
  const [controlLocked, setControlLocked] = useState<boolean>(!!SHELL.controlLocked);
  const [controlBusy, setControlBusy] = useState(false);
  // 🔴 결함 ⑨ — 지금 서버에 붙어 있는지. `undefined` = 아직 모름(기동 직후 — 경고를 띄우지 않는다).
  // 종전엔 코드만 있으면 "접속 받을 수 있는" 화면이었다. 서버가 이 기기를 거부 중이어도(Mac 실기) 그랬다.
  const [serverOnline, setServerOnline] = useState<boolean | undefined>(undefined);
  // 로그인 시 자동 실행(macOS 무인 대기). 스위치는 껍데기가 **적용한 결과**로만 그린다(실패 시 원래 값 유지).
  const [autostart, setAutostart] = useState<boolean>(!!SHELL.autostartEnabled);
  const [autostartBusy, setAutostartBusy] = useState(false);
  const [autostartError, setAutostartError] = useState<string | undefined>(undefined);
  // macOS 권한 상태 — 공용 훅(빠진 목록 · 설정 열기 · 앱 다시 시작).
  const { missing, openSettings, restartApp } = useMacPerms();
  // 조직 등록(claim). 결과는 서버 왕복 + **사람의 수락**을 거치므로 두 번 온다(접수 → 승인/거부).
  const [claimEmail, setClaimEmail] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimNote, setClaimNote] = useState<{ tone: 'ok' | 'warn'; text: string } | undefined>();
  // 응답 대기 타이머(무응답 감지). 결과가 오면 지운다 — 남겨 두면 성공 뒤에 경고가 덮어쓴다.
  const claimTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // 등록된 조직(§14.18). 탈퇴 중인 조직 id — 버튼 잠금용(목록은 서버 푸시로만 바뀐다).
  const [orgs, setOrgs] = useState<OrgEntry[]>([]);
  const [leavingOrgId, setLeavingOrgId] = useState<string | undefined>(undefined);
  const [orgNote, setOrgNote] = useState<{ tone: 'ok' | 'warn'; text: string } | undefined>();
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // 껍데기(Tauri)에서 실행 중이면 에이전트의 정체성 갱신 이벤트를 구독해 세션PW·무인 상태를 실시간 반영.
  useEffect(() => {
    if (!IS_SHELL) return;

    /** 정체성 페이로드 한 건을 화면 상태에 반영(이벤트·최초 조회 공통). */
    const apply = (p: Identity | null | undefined) => {
      if (!p) return;
      // 기기 코드는 `useMyDeviceCode` 가 따로 받는다.
      // ⚠️ 값이 **비어 오는 경우도 반영**해야 한다(무인 서비스가 멈추면 빈 정체성이 온다).
      // 종전처럼 truthy 일 때만 반영하면 서비스가 죽은 뒤에도 옛 비밀번호를 계속 보여 준다 — 거짓말이다.
      if ('sessionPassword' in p) setSessionPw(p.sessionPassword || undefined);
      if (typeof p.unattendedSet === 'boolean') setUnattendedSet(p.unattendedSet);
      if (typeof p.trustedCount === 'number') setTrustedCount(p.trustedCount);
      // 스위치는 **에이전트가 적용한 값**으로만 그린다(낙관적 갱신 금지). view-only 로 고정된 기기는
      // 켜기 요청이 반영되지 않는데, 화면만 먼저 켜 두면 "켰는데 상대가 조작 못 한다" 가 된다.
      if (typeof p.controlAllowed === 'boolean') setControlAllowed(p.controlAllowed);
      if (typeof p.controlLocked === 'boolean') setControlLocked(p.controlLocked);
      if (Array.isArray(p.orgs)) {
        const next = p.orgs;
        setOrgs(next);
        // 탈퇴 중이던 조직이 목록에서 빠졌다 = 탈퇴가 끝났다(원천은 서버 푸시).
        setLeavingOrgId((cur) => {
          if (cur && !next.some((o) => o.orgId === cur)) {
            if (leaveTimer.current) {
              clearTimeout(leaveTimer.current);
              leaveTimer.current = undefined;
            }
            return undefined;
          }
          return cur;
        });
      }
      setRegenerating(false);
      setUnattendedBusy(false);
      // 정체성이 왔다 = 정책 명령이 적용됐다 → 무응답 경고를 취소한다.
      if (unattTimer.current) {
        clearTimeout(unattTimer.current);
        unattTimer.current = undefined;
      }
      setControlBusy(false);
    };

    const un = listen<Identity>(IDENTITY_EVENT, (e) => apply(e.payload));
    // 서버 연결 상태(결함 ⑨). 정체성과 같은 이유로 구독 직후 최신값을 직접 물어본다(리스너 전 갱신 유실 방지).
    const unConn = listen<{ online?: boolean }>('rs-conn', (e) => {
      if (typeof e.payload?.online === 'boolean') setServerOnline(e.payload.online);
    });
    invoke<boolean | null>('current_conn')
      .then((v) => {
        if (typeof v === 'boolean') setServerOnline(v);
      })
      .catch(() => {});
    // 조직 등록 진행 상황. `pending:true` = 요청 접수(수락 대기), `pending:false` = 사용자가 처리함.
    const unClaim = listen<{
      ok?: boolean;
      pending?: boolean;
      approved?: boolean;
      targetUserName?: string;
      message?: string;
      kind?: string;
    }>('rs-claim', (e) => {
      const p = e.payload ?? {};
      // 조직 탈퇴 결과(§14.18)도 같은 통로로 온다(무인 서비스 PC 까지 닿는 길이 이것뿐이라) — 등록 요청 표시와 섞지 않는다.
      if (p.kind === 'leave') {
        if (leaveTimer.current) {
          clearTimeout(leaveTimer.current);
          leaveTimer.current = undefined;
        }
        setLeavingOrgId(undefined);
        setOrgNote(
          p.ok
            ? { tone: 'ok', text: '조직에서 탈퇴했습니다.' }
            : { tone: 'warn', text: p.message || '탈퇴에 실패했습니다.' },
        );
        return;
      }
      // 답이 왔으니 무응답 경고는 취소한다(승인/거부는 사람이 처리한 뒤라 한참 뒤에 온다 — 그때는 타이머가 이미 없다).
      if (claimTimer.current) {
        clearTimeout(claimTimer.current);
        claimTimer.current = undefined;
      }
      setClaimBusy(false);
      if (!p.ok) {
        setClaimNote({ tone: 'warn', text: p.message || '등록 요청에 실패했습니다.' });
      } else if (p.pending) {
        const who = p.targetUserName ? `${p.targetUserName} 님` : '담당자';
        setClaimNote({ tone: 'ok', text: `${who}에게 등록 요청을 보냈습니다. 수락하면 등록됩니다.` });
      } else if (p.approved) {
        setClaimNote({ tone: 'ok', text: '등록이 승인되었습니다. 이제 담당자 계정에서 이 기기가 보입니다.' });
        setClaimEmail('');
      } else {
        setClaimNote({ tone: 'warn', text: '등록 요청이 거부되었습니다.' });
      }
    });
    // 무인 비밀번호 확인·보기 결과.
    //   확인: { set, ok }  — set=false 는 "설정 안 됨"(틀림과 구분해야 한다)
    //   보기: { available, password } — available=false 는 구버전에 설정해 평문이 없는 기기
    const unUnatt = listen<{
      set?: boolean;
      ok?: boolean;
      available?: boolean;
      password?: string;
    }>('rs-unatt', (e) => {
      const p = e.payload ?? {};
      setUnattendedBusy(false);
      if (unattTimer.current) {
        clearTimeout(unattTimer.current);
        unattTimer.current = undefined;
      }
      if ('available' in p) {
        if (p.available && p.password) {
          setUnattShown(p.password);
          setUnattNote(undefined);
        } else {
          setUnattShown(undefined);
          setUnattNote({
            tone: 'warn',
            text: '이 기기에는 저장된 값이 없어 보여 줄 수 없습니다(예전 버전에서 설정). 아래 “내가 아는 값이 맞는지 확인”으로 맞는지 볼 수 있고, [변경] 으로 새로 정하면 이후에는 보기가 됩니다.',
          });
        }
        return;
      }
      if (p.set === false) {
        setUnattNote({ tone: 'warn', text: '무인 비밀번호가 설정돼 있지 않습니다.' });
      } else if (p.ok) {
        setUnattNote({ tone: 'ok', text: '맞습니다 — 이 비밀번호로 무인 접속됩니다.' });
      } else {
        setUnattNote({ tone: 'warn', text: '다릅니다. 기억하는 값이 아니면 새로 설정하세요.' });
      }
    });

    // 🔴 **리스너를 달기 전에 온 갱신은 사라진다.** 창은 첫 정체성을 받은 뒤 만들어지고 웹뷰가
    // 뜨는 데 또 수백 ms 가 걸리는데, 무인 서비스에 붙는 모드에서는 진짜 ID 가 바로 그 틈에 도착한다.
    // 서비스도 어댑터도 상태를 "바뀔 때만" 보내므로 두 번째 기회가 없어 창이 영영 빈 채로 남았다.
    // ⇒ 구독 직후 껍데기가 들고 있는 최신값을 **직접 물어본다**(이벤트를 놓쳤어도 복구된다).
    invoke<Identity | null>(IDENTITY_QUERY).then(apply).catch(() => {});

    return () => {
      un.then((f) => f());
      unConn.then((f) => f());
      unClaim.then((f) => f());
      unUnatt.then((f) => f());
      if (claimTimer.current) clearTimeout(claimTimer.current);
      if (unattTimer.current) clearTimeout(unattTimer.current);
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, []);

  /**
   * 조직 탈퇴(§14.18) — 확인 받고 에이전트에 보낸다. 성공하면 서버가 새 목록을 밀어 화면에서 빠진다.
   * 무응답 감시는 등록 요청과 같은 이유(중간 층이 삼키면 버튼이 굳는다)로 둔다.
   */
  const leaveOrg = async (org: OrgEntry) => {
    const ok = await confirmAction({
      title: `'${org.orgName || '이 조직'}'에서 탈퇴할까요?`,
      message: '그 조직의 담당자는 더 이상 이 기기를 목록에서 보거나 접속할 수 없습니다. 다시 등록하려면 등록 요청을 새로 보내야 합니다.',
      confirmLabel: '탈퇴',
      danger: true,
    });
    if (!ok) return;
    setOrgNote(undefined);
    setLeavingOrgId(org.orgId);
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = undefined;
      setLeavingOrgId(undefined);
      setOrgNote({ tone: 'warn', text: '응답이 없습니다. 잠시 후 다시 시도해 주세요.' });
    }, CLAIM_TIMEOUT_MS);
    try {
      await invoke('leave_org', { orgId: org.orgId });
    } catch (e) {
      if (leaveTimer.current) {
        clearTimeout(leaveTimer.current);
        leaveTimer.current = undefined;
      }
      setLeavingOrgId(undefined);
      setOrgNote({ tone: 'warn', text: String(e) });
    }
  };

  /**
   * [보기] 로 드러낸 비밀번호는 **스스로 다시 가려진다.**
   * 무인 비밀번호는 세션 비밀번호와 달리 오래 유효한 값이라, 확인하고 자리를 뜬 화면에 계속 떠 있으면
   * 지나가는 사람이 읽는다. 확인에 필요한 시간만 보여 주고 닫는다(다시 누르면 또 보인다).
   */
  useEffect(() => {
    if (!unattShown) return;
    const t = setTimeout(() => setUnattShown(undefined), UNATT_REVEAL_MS);
    return () => clearTimeout(t);
  }, [unattShown]);

  /**
   * 조직 등록 요청 — 담당자 이메일로 보낸다. 그 사람이 뷰어에서 수락해야 등록이 끝난다.
   * 실패도 이벤트(`rs-claim`)로 오므로 여기서는 **보내기 실패**(껍데기 거절)만 직접 처리한다.
   */
  const requestClaim = async () => {
    const email = claimEmail.trim();
    if (!email) return;
    setClaimBusy(true);
    setClaimNote(undefined);
    // 무응답 감시 — 명령은 받아졌는데 결과가 오지 않는 구성이 실제로 있었다(무인 서비스 PC).
    if (claimTimer.current) clearTimeout(claimTimer.current);
    claimTimer.current = setTimeout(() => {
      claimTimer.current = undefined;
      setClaimBusy(false);
      setClaimNote({
        tone: 'warn',
        text: '응답이 없습니다. 잠시 후 다시 시도해 주세요. 계속되면 앱(또는 무인 서비스)을 최신 버전으로 업데이트해야 합니다.',
      });
    }, CLAIM_TIMEOUT_MS);
    try {
      await invoke('request_org_claim', { email });
    } catch (e) {
      if (claimTimer.current) {
        clearTimeout(claimTimer.current);
        claimTimer.current = undefined;
      }
      setClaimBusy(false);
      setClaimNote({ tone: 'warn', text: String(e) });
    }
  };

  // 세션 비밀번호 재생성 요청(껍데기 command → 에이전트 stdin → 새 PW → rs-identity 이벤트).
  const regenerate = async () => {
    setRegenerating(true);
    try {
      await invoke('regenerate_session_password');
    } catch {
      setRegenerating(false);
    }
  };

  /**
   * 무인 명령을 보내기 직전에 **무응답 시계를 건다**(결과가 오면 리스너가 끈다).
   *
   * 답이 오지 않아도 화면이 스스로 풀리게 하는 것이 목적이다 — 잠긴 버튼만 남으면
   * 사용자는 앱을 강제로 껐다 켜는 것 말고 할 수 있는 일이 없다.
   */
  const armUnattWait = () => {
    if (unattTimer.current) clearTimeout(unattTimer.current);
    unattTimer.current = setTimeout(() => {
      unattTimer.current = undefined;
      setUnattendedBusy(false);
      setUnattNote({
        tone: 'warn',
        text: '이 기기의 에이전트가 응답하지 않았습니다. 잠시 뒤 다시 시도하거나 앱을 다시 시작해 보세요.',
      });
    }, UNATT_TIMEOUT_MS);
  };

  // 무인(Easy Access) 비밀번호 설정/변경(껍데기 command → 에이전트 stdin → 해시 저장 + 정책 갱신).
  // 설정과 변경은 **같은 명령**이다(덮어쓴다) — 화면만 모드로 구분한다.
  const setUnattended = async () => {
    if (!unattendedPw) return;
    setUnattNote(undefined);
    setUnattShown(undefined);
    setUnattendedBusy(true);
    armUnattWait();
    try {
      await invoke('set_unattended_password', { password: unattendedPw });
      setUnattendedPw('');
      setUnattMode('idle');
    } catch {
      setUnattendedBusy(false);
      if (unattTimer.current) {
        clearTimeout(unattTimer.current);
        unattTimer.current = undefined;
      }
    }
  };
  // 원격 입력 제어 허용/차단 — **진행 중인 세션에도 즉시 적용된다**(재접속 없음).
  const toggleControl = async () => {
    setControlBusy(true);
    try {
      await invoke('set_control_allowed', { allowed: !controlAllowed });
    } catch {
      setControlBusy(false);
    }
  };

  // 로그인 시 자동 실행 켜기/끄기 — 껍데기가 plist 를 쓰거나 지우고 적용 후 상태를 돌려준다.
  const toggleAutostart = async () => {
    setAutostartBusy(true);
    setAutostartError(undefined);
    try {
      setAutostart(await invoke<boolean>('set_autostart', { enabled: !autostart }));
    } catch (e) {
      // 대표 사례: 앱을 응용 프로그램 폴더로 옮기지 않고 실행(임시 격리 경로) — 껍데기가 이유를 문장으로 준다.
      setAutostartError(String(e));
    } finally {
      setAutostartBusy(false);
    }
  };

  const clearUnattended = async () => {
    // 확인 받고 진행(사용자 요청 2026-09-27) — 해제하면 무인 접속이 끊기고 "묻지 않고 접속" 허용 계정도 모두 풀린다.
    const ok = await confirmAction({
      title: '무인 접속을 해제할까요?',
      message: '이 PC 앞에 사람이 없으면 더는 원격으로 접속할 수 없습니다. "비밀번호 없이 접속" 허용 계정도 모두 풀립니다.',
      confirmLabel: '해제',
      danger: true,
    });
    if (!ok) return;
    setUnattendedBusy(true);
    armUnattWait();
    try {
      await invoke('clear_unattended_password');
      setUnattNote(undefined);
      setUnattShown(undefined);
      setUnattMode('idle');
      setUnattendedPw('');
    } catch {
      setUnattendedBusy(false);
      if (unattTimer.current) {
        clearTimeout(unattTimer.current);
        unattTimer.current = undefined;
      }
    }
  };

  /**
   * 무인 비밀번호 **확인** — 입력칸의 값이 설정된 것과 맞는지 묻는다(평문이 오가지 않는다).
   * 결과는 `rs-unatt` 이벤트로 온다(값의 주인이 에이전트·서비스라 여기서 즉시 답할 수 없다).
   */
  const verifyUnattended = async () => {
    if (!unattendedPw) return;
    setUnattNote(undefined);
    setUnattShown(undefined);
    setUnattendedBusy(true);
    armUnattWait();
    try {
      await invoke('verify_unattended_password', { password: unattendedPw });
    } catch (e) {
      setUnattendedBusy(false);
      if (unattTimer.current) {
        clearTimeout(unattTimer.current);
        unattTimer.current = undefined;
      }
      setUnattNote({ tone: 'warn', text: (e as Error).message || '확인에 실패했습니다.' });
    }
  };

  /**
   * 무인 비밀번호 **보기** — 저장된 값을 화면에 잠시 보여 준다.
   * 평문은 이 기기 안에서만 오간다(서버로 나가지 않고, 앱 로그에서도 가려진다).
   */
  const showUnattended = async () => {
    setUnattNote(undefined);
    setUnattendedBusy(true);
    armUnattWait();
    try {
      await invoke('show_unattended_password');
    } catch (e) {
      setUnattendedBusy(false);
      if (unattTimer.current) {
        clearTimeout(unattTimer.current);
        unattTimer.current = undefined;
      }
      setUnattNote({ tone: 'warn', text: (e as Error).message || '조회에 실패했습니다.' });
    }
  };

  // 서버가 받아 주지 않는 동안은 이 코드로 접속이 **안 된다** — 코드를 흐리게 하고 경고한다(결함 ⑨).
  const offline = serverOnline === false;

  // 권한 설정 화면 열기 / 앱 재시작(화면 기록은 재시작해야 적용된다 — perm_mac.rs "반영 시점").


  return {
    deviceCode,
    sessionPw,
    regenerating,
    regenerate,
    offline,
    missing,
    openSettings,
    restartApp,
    controlAllowed,
    controlLocked,
    controlBusy,
    toggleControl,
    unattendedSet,
    trustedCount,
    unattendedPw,
    setUnattendedPw,
    unattendedBusy,
    unattNote,
    setUnattNote,
    unattShown,
    setUnattShown,
    unattMode,
    setUnattMode,
    setUnattended,
    clearUnattended,
    verifyUnattended,
    showUnattended,
    autostart,
    autostartBusy,
    autostartError,
    toggleAutostart,
    claimEmail,
    setClaimEmail,
    claimBusy,
    claimNote,
    requestClaim,
    orgs,
    leavingOrgId,
    orgNote,
    leaveOrg,
  };
}

/** 훅이 돌려주는 값(화면 컴포넌트 props). */
export type MyConnection = ReturnType<typeof useMyConnection>;
